import { Link } from "react-router-dom";
import type { Work } from "@/types";
import { formatScore } from "@/lib/content";

interface WorkCardProps {
  work: Work;
  layout?: "grid" | "list";
}

export default function WorkCard({ work, layout = "grid" }: WorkCardProps) {
  if (layout === "list") {
    return (
      <Link
        to={`/detail/${work.id}`}
        className="group flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-background-100 sm:flex-row"
      >
        <div className="relative h-56 w-full shrink-0 overflow-hidden rounded-lg bg-background-200 sm:h-40 sm:w-28">
          <img
            src={work.poster}
            alt={`${work.title} 剧照海报`}
            title={`${work.title} 影视剧剧情介绍与角色分析`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">{work.type}</span>
            <span className="text-xs text-foreground-500">{work.region} · {work.year}</span>
          </div>
          <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
            {work.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{work.synopsis}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-500">
            <span className="font-heading text-base font-bold text-accent-600">{formatScore(work.rating)}</span>
            <span>导演 {work.director}</span>
            <span className="truncate">{work.genres.join(" / ")}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${work.id}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-200">
        <img
          src={work.poster}
          alt={`${work.title} 剧照海报`}
          title={`${work.title} 影视剧剧情介绍与角色分析`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-md bg-primary-500/95 px-2.5 py-1 text-xs font-medium text-background-50">
          {work.type}
        </span>
        <span className="absolute right-3 top-3 rounded-md bg-background-950/70 px-2.5 py-1 font-heading text-xs font-bold text-secondary-300">
          {formatScore(work.rating)}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {work.title}
        </h3>
        <p className="mt-1.5 text-xs text-foreground-500">{work.region} · {work.year} · {work.genres.slice(0, 2).join(" / ")}</p>
        <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">{work.synopsis}</p>
        <div className="mt-3 flex items-center gap-1.5 pt-3 text-xs text-primary-600">
          <span className="font-medium">查看剧情与角色分析</span>
          <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
        </div>
      </div>
    </Link>
  );
}